import { createFileRoute } from '@tanstack/react-router';
import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import PageTransition from '@/components/ui/PageTransition';
import { Card, Button, Typography, Table, App, Select, Flex, Tooltip, Empty, theme } from 'antd';
import { ThunderboltOutlined, PlusOutlined, ClearOutlined } from '@ant-design/icons';
import { DetailPageHeader } from '@/components/ui/DetailPageHeader';
import { FilterBar } from '@/components/ui/FilterBar';
import { clientByIdQueryOptions, useClientByIdQuery } from '@/api/auth/queries';
import {
  useGetEventsQuery,
  useGetTriggerableFlowsQuery,
  useGetEventMappingsQuery
} from '@/api/flows/queries';
import {
  useUpdateEventMappingMutation,
  useDeleteEventMappingMutation
} from '@/api/flows/mutations';
import type { EventMapping } from '@/api/flows/interfaces';
import { getApiErrorMessage } from '@/utils/apiError';
import {
  AddEventMappingModal,
  DeleteEventMappingModal,
  useEventDescription,
  useEventTriggerColumns
} from '@/components/event-triggers';

const { Text } = Typography;

export const Route = createFileRoute('/admin/tenants/$tenantId/event-triggers')({
  component: EventTriggersPage,
  loader: async ({ context, params }) => {
    await context.queryClient.prefetchQuery(clientByIdQueryOptions(params.tenantId));
  }
});

function EventTriggersPage() {
  const { message } = App.useApp();
  const { t } = useTranslation('admin');
  const { token } = theme.useToken();
  const { tenantId } = Route.useParams();
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [mappingToDelete, setMappingToDelete] = useState<EventMapping | null>(null);
  const [updatingMappingId, setUpdatingMappingId] = useState<string | null>(null);
  const getEventDescription = useEventDescription();

  const [filterEventType, setFilterEventType] = useState<string | undefined>();
  const [filterFlowId, setFilterFlowId] = useState<string | undefined>();
  const [filterTriggerNodeId, setFilterTriggerNodeId] = useState<string | undefined>();

  const { data: clientResponse } = useClientByIdQuery(tenantId);
  const clientName = clientResponse?.client.name ?? tenantId;

  const { data: eventsData, isLoading: eventsLoading } = useGetEventsQuery(!!tenantId);
  const { data: triggerableFlows = [], isLoading: flowsLoading } = useGetTriggerableFlowsQuery(tenantId);
  const { data: mappings = [], isLoading: mappingsLoading } = useGetEventMappingsQuery(tenantId, null);

  const updateMutation = useUpdateEventMappingMutation();
  const deleteMutation = useDeleteEventMappingMutation();

  const events = eventsData?.events ?? [];

  /** Flips a mapping between active and inactive. */
  const handleToggleActive = async (mapping: EventMapping) => {
    setUpdatingMappingId(mapping.id);
    try {
      await updateMutation.mutateAsync({
        clientId: tenantId,
        eventType: mapping.eventType,
        flowId: mapping.flowId,
        triggerNodeId: mapping.triggerNodeId,
        isActive: !mapping.isActive,
        companyId: mapping.companyId
      });
      message.success(mapping.isActive ? t('tenants.detail.eventTriggers.disabledSuccess') : t('tenants.detail.eventTriggers.enabledSuccess'));
    } catch (err: any) {
      message.error(getApiErrorMessage(err, t('tenants.detail.eventTriggers.updateError')));
    } finally {
      setUpdatingMappingId(null);
    }
  };

  /** Deletes the mapping and closes the confirmation dialog. */
  const handleDelete = async (mapping: EventMapping) => {
    try {
      await deleteMutation.mutateAsync({
        clientId: tenantId,
        eventType: mapping.eventType,
        flowId: mapping.flowId,
        triggerNodeId: mapping.triggerNodeId,
        companyId: mapping.companyId ?? undefined
      });
      message.success(t('tenants.detail.eventTriggers.deleteSuccess'));
      setMappingToDelete(null);
    } catch (err: any) {
      message.error(getApiErrorMessage(err, t('tenants.detail.eventTriggers.deleteError')));
    }
  };

  const flowNameById = Object.fromEntries(triggerableFlows.map((f) => [f.flowId, f.name ?? f.flowId]));

  const uniqueTriggerNodeIds = useMemo(
    () => [...new Set(mappings.map((m) => m.triggerNodeId))].sort(),
    [mappings]
  );

  const mappingsFiltered = useMemo(() => {
    return mappings.filter((m) => {
      if (filterEventType != null && m.eventType !== filterEventType) return false;
      if (filterFlowId != null && m.flowId !== filterFlowId) return false;
      if (filterTriggerNodeId != null && m.triggerNodeId !== filterTriggerNodeId) return false;
      return true;
    });
  }, [mappings, filterEventType, filterFlowId, filterTriggerNodeId]);

  const hasActiveFilters = filterEventType != null || filterFlowId != null || filterTriggerNodeId != null;
  /** Resets the event type, flow and trigger node filters. */
  const clearFilters = () => {
    setFilterEventType(undefined);
    setFilterFlowId(undefined);
    setFilterTriggerNodeId(undefined);
  };

  const columns = useEventTriggerColumns({
    tenantId,
    flowNameById,
    updatingMappingId,
    deletingMappingId: deleteMutation.isPending ? mappingToDelete?.id ?? null : null,
    onToggleActive: handleToggleActive,
    onDeleteClick: setMappingToDelete
  });

  return (
    <PageTransition animation="fadeInUp">
      <DetailPageHeader
        icon={<ThunderboltOutlined />}
        title={t('tenants.detail.eventTriggers.title')}
        meta={
          <>
            <Text strong ellipsis={{ tooltip: true }} style={{ maxWidth: '100%' }}>{clientName}</Text>
            <Text type="secondary">{t('tenants.detail.eventTriggers.subtitle')}</Text>
          </>
        }
        actions={
          <Tooltip title={t('tenants.detail.eventTriggers.addMappingTooltip')}>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => setAddModalOpen(true)}>
              {t('tenants.detail.eventTriggers.addMapping')}
            </Button>
          </Tooltip>
        }
        backTo={`/admin/tenants/${tenantId}`}
        backLabel={t('tenants.detail.backToTenant')}
      />

      <FilterBar
        actions={
          hasActiveFilters ? (
            <Button icon={<ClearOutlined />} onClick={clearFilters}>
              {t('tenants.detail.eventTriggers.clearFilters')}
            </Button>
          ) : undefined
        }
      >
        <Select
          placeholder={t('tenants.detail.eventTriggers.allEvents')}
          aria-label={t('tenants.detail.eventTriggers.eventType')}
          allowClear
          value={filterEventType}
          onChange={setFilterEventType}
          options={events.map((e) => ({ label: e, value: e }))}
          style={{ flex: '1 1 200px', minWidth: 160 }}
          popupMatchSelectWidth={false}
          showSearch={{ optionFilterProp: ['label', 'value'] }}
          filterOption={(input, option) =>
            (option?.label ?? '').toString().toLowerCase().includes(input.toLowerCase())
          }
          optionRender={(option) => (
            <Flex vertical gap={0} style={{ maxWidth: 'min(420px, calc(100vw - 64px))', whiteSpace: 'normal' }}>
              <Text strong>{option.label}</Text>
              <Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
                {getEventDescription(String(option.value))}
              </Text>
            </Flex>
          )}
        />
        <Select
          placeholder={t('tenants.detail.eventTriggers.allFlows')}
          aria-label={t('tenants.detail.eventTriggers.flow')}
          allowClear
          value={filterFlowId}
          onChange={setFilterFlowId}
          options={triggerableFlows.map((f) => ({ label: f.name ?? f.flowId, value: f.flowId }))}
          style={{ flex: '1 1 220px', minWidth: 160 }}
          showSearch={{ optionFilterProp: ['label', 'value'] }}
          filterOption={(input, option) =>
            (option?.label ?? '').toString().toLowerCase().includes(input.toLowerCase())
          }
        />
        <Select
          placeholder={t('tenants.detail.eventTriggers.allNodes')}
          aria-label={t('tenants.detail.eventTriggers.triggerNode')}
          allowClear
          value={filterTriggerNodeId}
          onChange={setFilterTriggerNodeId}
          options={uniqueTriggerNodeIds.map((id) => ({ label: id, value: id }))}
          style={{ flex: '1 1 200px', minWidth: 160 }}
          showSearch={{ optionFilterProp: ['label', 'value'] }}
          filterOption={(input, option) =>
            (option?.label ?? '').toString().toLowerCase().includes(input.toLowerCase())
          }
        />
        {hasActiveFilters && (
          <Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
            {t('tenants.detail.eventTriggers.showingCount', {
              count: mappingsFiltered.length,
              total: mappings.length
            })}
          </Text>
        )}
      </FilterBar>

      <Card
        styles={{ body: { padding: 0 } }}
      >
        <Table
          dataSource={mappingsFiltered}
          columns={columns}
          rowKey="id"
          loading={mappingsLoading}
          pagination={false}
          size="middle"
          scroll={{ x: 'max-content' }}
          locale={{
            emptyText: (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                style={{ paddingBlock: token.paddingLG }}
                description={
                  <Flex vertical align="center" gap={token.marginXXS}>
                    <Text>{t('tenants.detail.eventTriggers.empty')}</Text>
                    <Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
                      {t('tenants.detail.eventTriggers.emptyHint')}
                    </Text>
                  </Flex>
                }
              >
                {!hasActiveFilters && (
                  <Button type="primary" icon={<PlusOutlined />} onClick={() => setAddModalOpen(true)}>
                    {t('tenants.detail.eventTriggers.addMapping')}
                  </Button>
                )}
              </Empty>
            )
          }}
        />
      </Card>

      <DeleteEventMappingModal
        mapping={mappingToDelete}
        flowName={mappingToDelete ? flowNameById[mappingToDelete.flowId] : undefined}
        loading={deleteMutation.isPending}
        onCancel={() => setMappingToDelete(null)}
        onConfirm={handleDelete}
      />

      <AddEventMappingModal
        open={addModalOpen}
        tenantId={tenantId}
        clientName={clientName}
        events={events}
        eventsLoading={eventsLoading}
        triggerableFlows={triggerableFlows}
        flowsLoading={flowsLoading}
        onClose={() => setAddModalOpen(false)}
      />
    </PageTransition>
  );
}
